import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
const NOTE_DEFAULT_LABEL = {
    info: ['ui.note.info', 'Note'],
    warn: ['ui.note.warn', 'Important'],
    tip: ['ui.note.tip', 'Tip'],
    watch: ['ui.note.watch', 'Shown on the watch'],
};
export const Note = ({ variant = 'info', label, className = '', children }) => {
    const { t } = useTranslation();
    const [key, fallback] = NOTE_DEFAULT_LABEL[variant];
    const cls = variant === 'warn' ? 'note-warn' : variant === 'tip' ? 'note-tip' : '';
    return (_jsxs("div", { className: `note ${cls} ${className}`.trim(), role: variant === 'warn' ? 'note' : undefined, children: [_jsx("span", { className: "note-label", children: label ?? t(key, fallback) }), typeof children === 'string' ? _jsx("p", { children: children }) : children] }));
};
/** Nummerierte Schritte. Jedes Kind ist ein Schritt. */
export const Steps = ({ children, className = '' }) => (_jsx("ol", { className: `steps ${className}`.trim(), children: React.Children.toArray(children)
        .filter(Boolean)
        .map((child, i) => (_jsx("li", { children: _jsx("div", { className: "step-body", children: typeof child === 'string' ? _jsx("p", { children: child }) : child }) }, i))) }));
export const MenuPath = ({ items }) => (_jsx("span", { className: "menu-path", children: items.map((item, i) => (_jsxs(React.Fragment, { children: [i > 0 && (_jsx("span", { className: "menu-path-sep", "aria-hidden": "true", children: "\u203A" })), _jsx("span", { className: "menu-path-item", children: item })] }, i))) }));
/** Button oder Menüpunkt in einer App (z. B. „Installieren“) */
export const Ui = ({ children }) => _jsx("span", { className: "ui-label", children: children });
/** Text, den die Uhr anzeigt (z. B. „no data“) */
export const WatchText = ({ children }) => (_jsx("span", { className: "watch-text", children: children }));
/** Name einer Einstellung, so wie er in der Connect IQ App steht */
export const Setting = ({ children }) => (_jsx("span", { className: "setting-name", children: children }));
export const Tag = ({ tone = 'accent', children, }) => _jsx("span", { className: `tag ${tone === 'signal' ? 'tag-signal' : tone === 'muted' ? 'tag-muted' : ''}`, children: children });
export const Eyebrow = ({ children, as = 'span' }) => React.createElement(as, { className: 'ui-eyebrow' }, children);
export const Breadcrumbs = ({ items }) => {
    const { t } = useTranslation();
    return (_jsx("nav", { "aria-label": t('ui.breadcrumbs', 'Breadcrumb'), children: _jsx("ol", { className: "breadcrumbs", children: items.map((c, i) => (_jsx("li", { children: c.to && i < items.length - 1 ? (_jsx(Link, { to: c.to, children: c.label })) : (_jsx("span", { "aria-current": i === items.length - 1 ? 'page' : undefined, children: c.label })) }, i))) }) }));
};
/**
 * Wandelt einfache Auszeichnungen in übersetzten Texten in Bausteine um:
 * [ui]Installieren[/ui], [watch]no data[/watch], [set]⛅ Wetteranbieter[/set],
 * [b]fett[/b], [code]D, d.m.Y[/code] und Links [link=/guides/weather]Text[/link].
 * So bleiben Übersetzungen reine Strings, ohne HTML.
 */
const RICH_RE = /\[(ui|watch|set|b|code|link)(?:=([^\]]+))?\]([\s\S]*?)\[\/\1\]/g;
export function rich(text) {
    const out = [];
    let last = 0;
    let m;
    let k = 0;
    RICH_RE.lastIndex = 0;
    while ((m = RICH_RE.exec(text)) !== null) {
        if (m.index > last)
            out.push(text.slice(last, m.index));
        const [, tag, arg, inner] = m;
        const key = `r${k++}`;
        switch (tag) {
            case 'ui':
                out.push(_jsx(Ui, { children: inner }, key));
                break;
            case 'watch':
                out.push(_jsx(WatchText, { children: inner }, key));
                break;
            case 'set':
                out.push(_jsx(Setting, { children: inner }, key));
                break;
            case 'b':
                out.push(_jsx("strong", { children: inner }, key));
                break;
            case 'code':
                out.push(_jsx("code", { children: inner }, key));
                break;
            case 'link':
                out.push(arg && arg.startsWith('/') ? (_jsx(Link, { to: arg, className: "inline-link", children: inner }, key)) : (_jsx("a", { href: arg, target: "_blank", rel: "noopener noreferrer", className: "inline-link", children: inner }, key)));
                break;
        }
        last = m.index + m[0].length;
    }
    if (last < text.length)
        out.push(text.slice(last));
    return out;
}
/** Entfernt das Markup (für Meta-Texte, strukturierte Daten, Suche). */
export const plainText = (text) => text.replace(/\[(?:\/)?(?:ui|watch|set|b|code|link)(?:=[^\]]*)?\]/g, '');
/** Absatz mit Auszeichnungen aus einem Übersetzungsstring; Leerzeilen trennen Absätze. */
export const RichText = ({ text, className }) => (_jsx(_Fragment, { children: text.split(/\n\s*\n/).map((para, i) => (_jsx("p", { className: className, children: rich(para) }, i))) }));
